if (!customElements.get('scrolling-promotion')) {
  class ScrollingPromotion extends HTMLElement {
    constructor() {
      super();
    }

    /* marca | 15/04/2026 | moved init logic from constructor to connectedCallback for Safari/iOS compatibility — constructor runs before children are available in the DOM */
    connectedCallback() {
      if (this._initialized) return;
      this.promotion = this.querySelector('.promotion');
      if (!this.promotion) return;
      this._initialized = true;

      this.pauseOnHover = this.hasAttribute('data-pause-on-hover');
      /* marca | 15/04/2026 | respect prefers-reduced-motion for auto-scroll only — section should still be interactive (draggable) and clone content to fill the row */
      this.reduceMotion = FoxTheme.config.motionReduced;

      /* marca | 15/04/2026 | explicit state flags prevent race conditions between IntersectionObserver, hover, and drag consumers */
      this.isVisible = false;
      this.isHovered = false;
      this.isDragging = false;
      this.startX = 0;
      this.scrollOffset = 0;

      FoxTheme.Motion.inView(this, this.init.bind(this), { margin: '200px 0px 200px 0px' });
    }

    init() {
      if (this.childElementCount === 1) {
        this.promotion.classList.add('promotion--animated');

        for (let index = 0; index < 10; index++) {
          this.clone = this.promotion.cloneNode(true);
          this.appendChild(this.clone);
        }

        /* marca | 15/04/2026 | only enable auto-scroll when prefers-reduced-motion is not set — clones and drag are always available, animation class is always added so drag handlers can query .promotion--animated, but isVisible stays false (no observer) so updatePlayState keeps it permanently paused */
        if (!this.reduceMotion) {

          const observer = new IntersectionObserver(
            (entries) => {
              entries.forEach((entry) => {
                this.isVisible = entry.isIntersecting;
                this.updatePlayState();
              });
            },
            { rootMargin: '0px 0px 50px 0px' }
          );

          observer.observe(this);

          /* marca | 15/04/2026 | use pointerenter/pointerleave filtered to non-touch — prevents simulated mouseenter from touch taps permanently sticking isHovered as true on mobile */
          if (this.pauseOnHover) {
            this.addEventListener('pointerenter', (e) => {
              if (e.pointerType === 'touch') return;
              this.isHovered = true;
              this.updatePlayState();
            });
            this.addEventListener('pointerleave', (e) => {
              if (e.pointerType === 'touch') return;
              this.isHovered = false;
              this.updatePlayState();
            });
          }
        }

        /* marca | 15/04/2026 | unified pointer-event drag — always available regardless of motion preference */
        this.initDragScrolling();
      }
    }

    /* marca | 15/04/2026 | centralised play/pause — only plays when visible AND not hovered AND not being dragged */
    updatePlayState() {
      if (this.isVisible && !this.isHovered && !this.isDragging) {
        this.classList.remove('scrolling-promotion--paused');
      } else {
        this.classList.add('scrolling-promotion--paused');
      }
    }

    initDragScrolling() {
      this.onDragMoveBound = this.onDragMove.bind(this);
      this.onDragEndBound = this.onDragEnd.bind(this);
      this.addEventListener('pointerdown', this.onDragStart.bind(this));
    }

    /* marca | 15/04/2026 | pointerdown starts tracking — actual drag only begins after a 5px threshold so taps and link clicks still work */
    onDragStart(e) {
      if (e.button !== 0) return;

      this.startX = e.clientX;
      this.hasDragged = false;
      this.captureCurrentOffset();

      document.addEventListener('pointermove', this.onDragMoveBound);
      document.addEventListener('pointerup', this.onDragEndBound);
      document.addEventListener('pointercancel', this.onDragEndBound);
    }

    onDragMove(e) {
      const deltaX = e.clientX - this.startX;

      /* marca | 15/04/2026 | 5px dead zone before committing to drag — preserves tap/click behaviour on links and cards */
      if (!this.hasDragged && Math.abs(deltaX) > 5) {
        this.hasDragged = true;
        this.isDragging = true;
        this.updatePlayState();
        this.classList.add('scrolling-promotion--dragging');

        const promotions = this.querySelectorAll('.promotion--animated');
        promotions.forEach((promo) => {
          promo.style.transform = `translate3d(${this.scrollOffset}px, 0, 0)`;
          promo.style.animationName = 'none';
        });
      }

      if (this.hasDragged) {
        this.applyManualTranslate(this.scrollOffset + deltaX);
        e.preventDefault();
      }
    }

    onDragEnd(e) {
      document.removeEventListener('pointermove', this.onDragMoveBound);
      document.removeEventListener('pointerup', this.onDragEndBound);
      document.removeEventListener('pointercancel', this.onDragEndBound);

      if (!this.hasDragged) return;
      this.classList.remove('scrolling-promotion--dragging');

      /* marca | 15/04/2026 | suppress the click event that fires after drag release so links aren't accidentally followed */
      this.addEventListener('click', (ev) => { ev.preventDefault(); ev.stopPropagation(); }, { once: true, capture: true });

      const deltaX = e.clientX - this.startX;
      this.resumeAnimationFromOffset(this.scrollOffset + deltaX);
    }

    /* marca | 15/04/2026 | use getBoundingClientRect — reliably returns the actual visual position mid-animation on all browsers including iOS Safari */
    captureCurrentOffset() {
      const firstPromotion = this.querySelector('.promotion--animated');
      if (!firstPromotion) return;
      const promoRect = firstPromotion.getBoundingClientRect();
      const parentRect = this.getBoundingClientRect();
      this.scrollOffset = promoRect.left - parentRect.left;
    }

    applyManualTranslate(px) {
      const promotions = this.querySelectorAll('.promotion--animated');
      promotions.forEach((promo) => {
        promo.style.transform = `translate3d(${px}px, 0, 0)`;
      });
    }

    /* marca | 15/04/2026 | restart CSS animation with a negative delay so it picks up from the user's release position — all 10 clones are identical so any cycle-aligned position is visually seamless */
    resumeAnimationFromOffset(offsetPx) {
      const promoWidth = this.promotion.scrollWidth;
      const duration = parseFloat(getComputedStyle(this).getPropertyValue('--duration'));

      const normalizedOffset = ((Math.abs(offsetPx) % promoWidth) + promoWidth) % promoWidth;
      const progress = normalizedOffset / promoWidth;
      const delay = -(progress * duration);

      const promotions = this.querySelectorAll('.promotion--animated');

      /* marca | 15/04/2026 | force reflow while animationName is still 'none' (set in onDragMove) so browser commits the stopped state before we restart */
      void this.offsetHeight;

      promotions.forEach((promo) => {
        promo.style.transform = '';
        promo.style.animationDelay = `${delay}s`;
        promo.style.animationName = '';
      });

      this.isDragging = false;
      this.updatePlayState();
    }
  }
  customElements.define('scrolling-promotion', ScrollingPromotion);
}
